import { useQuery } from '@apollo/client';
import { GetLatestOversightRatingsByDocumentIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

import { extractCmaCompletedAt, isLater } from '@/utils/oversightUtils';

export type OversightDocumentRatingLeaf = {
  Rating: number | null;
  CompletedAt: string | null;
};

export type OversightRatingsByDocument = Map<
  string,
  OversightDocumentRatingLeaf
>;

const EMPTY_MAP: OversightRatingsByDocument = new Map();

/**
 * Fetches the latest oversight rating per document for the given document Ids,
 * keyed by DocumentId.
 *
 * "Latest" is determined by the linked compliance_monitoring_assessment's
 * ActualCompletionDate. The leaf query filters to completed CMAs with a non-null
 * completion date at both the leaf-eligibility level and the inner cmaParent
 * selection — re-applying at the inner level prevents attaching a stale CMA
 * date to an otherwise eligible row.
 *
 * Like risks and obligations (and unlike controls), `document_second_line_result`
 * has no direct ParentDocumentId column — a finding can attach to multiple
 * documents via the `parents` array_relationship. `documentParent` is
 * server-filtered to ParentId ∈ $DocumentIds; the client fans the leaf out
 * across each matching parent. In practice the backend creates one leaf per
 * DocumentId, but the fan-out is correct either way.
 *
 * Skips the query when oversight viewing is disabled or no documents are
 * visible.
 */
export const useLatestOversightRatingsByDocuments = (params: {
  documentIds: string[];
  isEnabled: boolean;
}): {
  ratingsByDocument: OversightRatingsByDocument;
  loading: boolean;
} => {
  const { documentIds, isEnabled } = params;
  const skip = !isEnabled || documentIds.length === 0;

  const { data, loading } = useQuery(
    GetLatestOversightRatingsByDocumentIdsDocument,
    {
      skip,
      variables: { DocumentIds: documentIds },
      fetchPolicy: 'cache-and-network',
    }
  );

  const ratingsByDocument = useMemo<OversightRatingsByDocument>(() => {
    if (!data) {
      return EMPTY_MAP;
    }

    const latestByDocument: OversightRatingsByDocument = new Map();

    for (const row of data.document_second_line_result) {
      const completedAt = extractCmaCompletedAt(row.cmaParent);
      const leaf: OversightDocumentRatingLeaf = {
        Rating: row.Rating ?? null,
        CompletedAt: completedAt,
      };
      for (const dp of row.documentParent) {
        const existing = latestByDocument.get(dp.ParentId);
        if (!existing || isLater(existing.CompletedAt, completedAt)) {
          latestByDocument.set(dp.ParentId, leaf);
        }
      }
    }

    return latestByDocument;
  }, [data]);

  return { ratingsByDocument, loading };
};
